import { Card } from "@/components/ui/Card"
import { formatMoneyShort } from "@/lib/money"
import Figure from "./Figure"
import { days, monthName, percentOf } from "./format"
import MonthProgressChart from "./MonthProgressChart"
import type { MonthProgressData } from "./types"

interface Props {
  currency: string | null
  recentDays: number
  month: MonthProgressData
}

// One plain sentence: where the month ends at the recent pace.
// None when the month has no plan.
function verdict({ currency, recentDays, month }: Props): string | null {
  const { plan, actual, per_day_now: perDayNow } = month
  if (plan == null) return null
  const money = (v: number) => formatMoneyShort(v, currency)
  const name = monthName(month.month)
  if (actual >= plan) return `${name} has already reached its plan of ${money(plan)}.`

  const endsAt = actual + perDayNow * month.days_left
  const endsPct = percentOf(endsAt, plan)
  const ends = `${money(endsAt)}${endsPct != null ? ` (${endsPct}% of plan)` : ""}`
  return `At the pace of the last ${days(recentDays)}, ${name} is projected to end at about ${ends}.`
}

// The current month: plan, actual so far, the pace now against the pace
// needed, and the running total day by day.
export default function MonthProgress(props: Props) {
  const { currency, recentDays, month } = props
  const { plan, actual, needed_per_day: needed, per_day_now: perDayNow } = month
  const money = (v: number) => formatMoneyShort(v, currency)
  const actualPct = percentOf(actual, plan)
  const sentence = verdict(props)
  return (
    <Card className="overflow-hidden">
      <div className="border-b px-4 py-3">
        <div className="text-sm font-semibold">{monthName(month.month)} Progress</div>
      </div>
      <div className="grid grid-cols-4 divide-x border-b">
        <Figure label="Month plan" value={plan == null ? "-" : money(plan)} />
        <Figure label="Actual so far" value={money(actual)}>
          {actualPct != null && <div>{actualPct}% of month plan</div>}
          <div>Day {month.today_day} of {month.days_in_month}</div>
        </Figure>
        <Figure label="Selling per day now" value={money(perDayNow)}>
          <div>Average of the last {days(recentDays)}</div>
        </Figure>
        <Figure
          label="Needed per day"
          value={needed == null ? "-" : money(needed)}
          tone={needed != null && needed > perDayNow ? "warning" : "default"}
        >
          {needed != null && <div>{month.days_left === 1 ? "For today" : `For the last ${days(month.days_left)}`}</div>}
        </Figure>
      </div>
      {sentence && <div className="border-b px-4 py-3 text-sm">{sentence}</div>}
      <div className="px-4 pb-2 pt-4">
        <MonthProgressChart currency={currency} month={month} />
      </div>
    </Card>
  )
}
